import { FormattedDuration } from '@10xLabs/react-web/components/FormattedDuration'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { useMutation } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useMemo, useState } from 'react'
import styled from 'styled-components'

import { FormattedDistance } from 'components/FormattedDistance'
import { AngleDownIcon } from 'components/Icons/AngleDown'
import { ClockIcon } from 'components/Icons/ClockIcon'
import { ReadinessScoreIcon } from 'components/Icons/ReadinessScoreIcon'
import { OutlinedPinIcon } from 'components/Icons/OutlinedPinIcon'
import { CalendarIcon } from 'components/Icons/CalendarIcon'
import { CostIcon } from 'components/Icons/CostIcon'
import { LockedIcon } from 'components/Icons/LockedIcon'
import { UnlockedIcon } from 'components/Icons/UnlockedIcon'
import { VisibilityOffOutlineIcon } from 'components/Icons/VisibilityOffOutlineIcon'
import { VisibilityOutlineIcon } from 'components/Icons/VisibilityOutlineIcon'
import { RouteActions } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList/RouteItem/Actions'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'
import { getAvailableDowsByRoutesAndLocations } from 'features/RoutingProjects/utils'
import { ToggleLockRouteModal } from 'features/RoutingProjects/components/ToggleLockRouteModal'
import { getDowByRouteGroups } from 'features/RoutingProjects/models'
import {
  CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_NAME,
  LOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE,
  UNLOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE
} from 'features/RoutingProjects/mutations'
import { IRoutingProjectPlanOptimization } from 'features/RoutingProjects/types'
import { RoutingProjectDow } from 'gql/generated/graphql'
import { RouteNameForm } from './RouteNameForm'

const RouteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const RouteNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconActionContainer = styled.span<{ isActive: boolean }>`
  display: ${({ isActive }): string => (isActive ? 'flex' : 'none')};
  cursor: pointer;
  padding: 16px 2px;
  margin: -16px -2px;
  color: ${({ isActive }): string => (isActive ? '#393939' : '#717171')};

  svg {
    fill: ${({ isActive }): string => (isActive ? '#393939' : '#717171')};
  }

  &:hover {
    color: #aaaaaa;

    svg {
      fill: #aaaaaa;
    }
  }
`

const StyledLockedIcon = styled(LockedIcon)`
  width: 13px;
  height: 16px;
`

const StyledUnlockedIcon = styled(UnlockedIcon)`
  width: 13px;
  height: 16px;
`

const StyledVisibilityOffOutlineIcon = styled(VisibilityOffOutlineIcon)`
  width: 17px;
  height: 16px;
`

const StyledVisibilityOutlineIcon = styled(VisibilityOutlineIcon)`
  width: 17px;
  height: 12px;
`

const RouteItemTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  gap: 8px;
  cursor: pointer;

  &:hover ${IconActionContainer} {
    display: flex;
  }
`

const RouteDataContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RouteData = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #393939;
`

const RouteDataIcon = styled.div`
  display: flex;

  svg {
    width: 16px;
    height: 16px;
    min-width: 16px;
    fill: #423f9a;
  }
`

const RouteName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #28265d;
  cursor: text;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledAngleDownIcon = styled(AngleDownIcon)<{ isOpen: boolean }>`
  fill: #717171;
  height: 6px !important;
  transform: ${({ isOpen }): string => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: 0.2s;
  width: 10px !important;
  margin: 0 2px;
`

const DowList = styled.div`
  display: flex;
  gap: 1px;
`

const DowItem = styled.div<{ isActive: boolean }>`
  text-transform: capitalize;
  color: ${({ isActive }): string => (isActive ? 'currentColor' : '#B0B0B0')};
`

const DOW_LIST = [
  RoutingProjectDow.Monday,
  RoutingProjectDow.Tuesday,
  RoutingProjectDow.Wednesday,
  RoutingProjectDow.Thursday,
  RoutingProjectDow.Friday,
  RoutingProjectDow.Saturday,
  RoutingProjectDow.Sunday
]

interface IRouteItemHeaderProps {
  routingProjectId: string
  currentOptimizationId: string
  route: IRoutingProjectPlanOptimization['routes'][0]
  isDetailsOpen: boolean
  setIsDetailsOpen: (isDetailsOpen: boolean) => void
  setIsOpenAddStopModal: (isOpenAddStopModal: boolean) => void
}

export const RouteItemHeader = ({
  routingProjectId,
  currentOptimizationId,
  route,
  isDetailsOpen,
  setIsDetailsOpen,
  setIsOpenAddStopModal
}: IRouteItemHeaderProps): ReactElement => {
  const { planModel, setPlanModel, hiddenRoutesIds, setHiddenRoutesIds, selectedDow } = usePlanContext()
  const [availableDowsToApplyChange, setAvailableDowsToApplyChange] = useState<RoutingProjectDow[]>([])
  const [isOpenToggleLockRouteModal, setIsOpenToggleLockRouteModal] = useState(false)
  const [isEditingRouteName, setIsEditingRouteName] = useState(false)
  const dowByRouteGroups = useMemo(() => {
    return getDowByRouteGroups(planModel.optimizations)
  }, [planModel.optimizations])
  const [lockRoute] = useMutation(LOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE)
  const [unlockRoute] = useMutation(UNLOCK_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE)
  const [changeRouteName] = useMutation(CHANGE_ROUTING_PROJECT_PLAN_OPTIMIZATION_ROUTE_NAME)

  const isRouteVisible = !hiddenRoutesIds?.includes(route.id)
  const toggleRouteDetails = (): void => {
    setIsDetailsOpen(!isDetailsOpen)
  }

  const handleToggleVisibility = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    const updatedHiddenRoutesIds = isRouteVisible
      ? [...hiddenRoutesIds, route.id]
      : hiddenRoutesIds.filter((routeId) => routeId !== route.id)
    setHiddenRoutesIds(updatedHiddenRoutesIds)
  }

  const handleToggleLockRoute = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    const newIsLocked = !route.isLocked

    setPlanModel({
      ...planModel,
      optimizations: planModel.optimizations.map((optimization) => {
        if (optimization.id === currentOptimizationId) {
          return {
            ...optimization,
            routes: optimization.routes.map((r) => {
              if (r.id === route.id) {
                return {
                  ...r,
                  isLocked: newIsLocked
                }
              }
              return r
            })
          }
        }
        return optimization
      })
    })

    let availableDows = getAvailableDowsByRoutesAndLocations({
      routes: [route],
      optimizations: planModel.optimizations,
      locations: [],
      shouldFilterLockedRoutes: false,
      groupedByLockedStatus: true
    })
    availableDows = availableDows.filter((dow) => dow !== selectedDow)
    if (!isEmpty(availableDows)) {
      setAvailableDowsToApplyChange(availableDows)
      setIsOpenToggleLockRouteModal(true)
    }

    if (newIsLocked) {
      lockRoute({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: planModel.id,
              optimizations: [
                {
                  id: currentOptimizationId,
                  route: {
                    id: route.id
                  }
                }
              ]
            }
          }
        }
      })
    } else {
      unlockRoute({
        variables: {
          input: {
            id: routingProjectId,
            plan: {
              id: planModel.id,
              optimizations: [
                {
                  id: currentOptimizationId,
                  route: {
                    id: route.id
                  }
                }
              ]
            }
          }
        }
      })
    }
  }

  const handleRouteNameDoubleClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    setIsEditingRouteName(true)
  }

  const handleRouteNameClick = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
  }

  const handleRouteNameClickAway = (): void => {
    setIsEditingRouteName(false)
  }

  const handleRouteNameSubmit = (newRouteName: string): void => {
    // Update planModel with new route name
    setPlanModel({
      ...planModel,
      optimizations: planModel.optimizations.map((optimization) => {
        if (optimization.id === currentOptimizationId) {
          return {
            ...optimization,
            routes: optimization.routes.map((r) => {
              if (r.id === route.id) {
                return {
                  ...r,
                  name: newRouteName
                }
              }
              return r
            })
          }
        }
        return optimization
      })
    })

    changeRouteName({
      variables: {
        input: {
          id: routingProjectId,
          plan: {
            id: planModel.id,
            optimization: {
              id: currentOptimizationId,
              route: {
                id: route.id,
                name: newRouteName
              }
            }
          }
        }
      }
    })

    setIsEditingRouteName(false)
  }

  const previousRouteGroupId = planModel.optimizations
    .find((optimization) => optimization.id === currentOptimizationId)
    ?.routes.find((r) => r.id === route.id)?.groupId
  const routeDows = dowByRouteGroups[previousRouteGroupId as string] || []

  return (
    <>
      <RouteItemTitle onClick={toggleRouteDetails}>
        <RouteDetails>
          <RouteNameContainer>
            {isEditingRouteName ? (
              <RouteNameForm
                routeName={route.name}
                onClickAway={handleRouteNameClickAway}
                onSubmit={handleRouteNameSubmit}
              />
            ) : (
              <RouteName onDoubleClick={handleRouteNameDoubleClick} onClick={handleRouteNameClick}>
                {route.name}
              </RouteName>
            )}
            <IconActionContainer onClick={handleToggleLockRoute} isActive={route.isLocked}>
              {route.isLocked ? <StyledLockedIcon /> : <StyledUnlockedIcon />}
            </IconActionContainer>
            <IconActionContainer onClick={handleToggleVisibility} isActive={!isRouteVisible}>
              {isRouteVisible ? <StyledVisibilityOutlineIcon /> : <StyledVisibilityOffOutlineIcon />}
            </IconActionContainer>
          </RouteNameContainer>
          <RouteDataContainer>
            <RouteData>
              <RouteDataIcon>
                <ReadinessScoreIcon />
              </RouteDataIcon>
              <FormattedDistance distance={route.distance} decimals={2} />
            </RouteData>
            <RouteData>
              <RouteDataIcon>
                <ClockIcon />
              </RouteDataIcon>
              <FormattedDuration duration={route.duration} />
            </RouteData>
            <RouteData>
              <RouteDataIcon>
                <OutlinedPinIcon />
              </RouteDataIcon>
              {route.stops.length}
            </RouteData>
            {planModel.dataset?.hasDow && (
              <RouteData>
                <RouteDataIcon>
                  <CalendarIcon />
                </RouteDataIcon>
                <DowList>
                  {DOW_LIST.map((dow, index) => {
                    const isActive = routeDows.includes(dow)
                    return (
                      <DowItem key={index} isActive={isActive}>
                        {dow.charAt(0)}
                      </DowItem>
                    )
                  })}
                </DowList>
              </RouteData>
            )}
            {!isEmpty(route.costs) && (
              <RouteData>
                <RouteDataIcon>
                  <CostIcon />
                </RouteDataIcon>
                <FormattedPrice value={route.costs?.minCost} /> - <FormattedPrice value={route.costs?.maxCost} />
              </RouteData>
            )}
          </RouteDataContainer>
        </RouteDetails>
        <Actions>
          <RouteActions setIsOpenAddStopModal={setIsOpenAddStopModal} route={route} />
          <StyledAngleDownIcon isOpen={isDetailsOpen} />
        </Actions>
      </RouteItemTitle>
      {isOpenToggleLockRouteModal && (
        <ToggleLockRouteModal
          availableDows={availableDowsToApplyChange}
          routeGroupId={route.groupId}
          onClose={(): void => {
            setIsOpenToggleLockRouteModal(false)
            setAvailableDowsToApplyChange([])
          }}
        />
      )}
    </>
  )
}
